import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { Link } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Card } from "@/components/ui/card";
import { Loading, ScreenBoundary } from "../components/states";
import {
  ActionBar,
  Button,
  QtyStepper,
  SectionHeading,
  Sheet,
  SubHeading,
  UrgencyTag,
  plural,
} from "../components/kit";
import { Textarea } from "@/components/ui/textarea";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import { useBusyAction } from "../lib/useBusyAction";
import { useViewer } from "../lib/viewer";

/**
 * Submit a request. **Two taps.**
 *
 * The whole product rests on this screen. Dee's staff refused the last tool
 * because it demanded too much input, so the constraint is not "few taps", it
 * is exactly two for a known item:
 *
 *   Tap 1 — the item row. Adds it to the draft at its usual quantity.
 *   Tap 2 — Send.
 *
 * Everything that makes that possible is deliberate:
 *
 * - The palette is **already open and populated** when the screen loads, from
 *   this clinic's own ordering history. No "start a request" button, no empty
 *   search box demanding a query first.
 * - Adding an item is **local state, not a mutation**. One network call happens,
 *   on Send. In an operatory with one bar of signal, a design that writes on
 *   every tap is a design that fails halfway through.
 * - Quantity defaults to what this clinic asked for last time, so the common
 *   case needs no adjustment at all.
 * - Everything else — quantity steppers, urgency, notes — is additive. Nothing
 *   is required to reach submitted.
 *
 * `tools/clickcount.mjs` measures this at 390px and fails above two.
 *
 * **Desktop is the same flow, laid out sideways.** At `lg` the palette and the
 * draft sit side by side rather than stacked, and the Send bar stops being
 * fixed and becomes the foot of the draft column — a full-width fixed bar at
 * 1280 ran underneath the sidebar and was, at best, accidentally invisible.
 * The DOM order is byte-for-byte the phone's, so tab order and the measured
 * tap count cannot differ between the two.
 */

/**
 * "This week's count is due", on the screen a clinic opens most.
 *
 * The information architecture says the count is v2's primary loop, but
 * `homeFor(assistant)` still lands on `/request` — the two-tap gate is measured
 * from app-open and does not move. So the reminder comes to the request screen
 * instead, under three rules it must not break:
 *
 * 1. **It costs the request path nothing.** Nothing here is on the way to
 *    sending a request: it is a line of text and a link, it takes no focus, and
 *    `clickcount` sees no extra interaction.
 * 2. **One line, always.** Two lines at 390px pushes the palette — the only
 *    thing anyone came here for — below the fold.
 * 3. **Nothing at all while the answer is unknown.** No reserved space, no
 *    skeleton: it appears when the status query lands, so a clinic that has
 *    already counted never sees the layout twitch.
 *
 * Dismissal is per week and per tab (`sessionStorage`), because next Monday it
 * is true again and a dismissal that outlived the week would silently switch
 * the reminder off for good.
 */
function CountDueBanner() {
  /* `built()` in router.tsx declares its `path` as `string`, so the registered
     route literals are only "/" and "$" — a `Link to="/count"` does not
     typecheck while a `string` does. Same reason kit's `<Tile to>` is typed
     `string`, and the same path the nav table hands `<Link>` in the shell. */
  const countPath: string = "/count";
  const viewer = useViewer();
  /* Warehouse is refused this query by name (`requireCounter`), and an owner
     has no clinic pinned — for either, Convex would throw during render and
     take the request screen down with it. */
  const counts = useQuery(
    api.counts.status,
    viewer.clinicId && (viewer.role === "assistant" || viewer.role === "manager")
      ? {}
      : "skip",
  );

  const key =
    counts && !counts.submitted ? `nexora-count-due-${counts.weekKey}` : null;
  /* Read once per week key rather than through an effect: an effect would
     paint the banner for one frame before hiding it again, which is the
     layout jump this component is not allowed to cause. */
  const alreadyDismissed = useMemo(() => {
    if (!key) return false;
    try {
      return window.sessionStorage.getItem(key) === "1";
    } catch {
      return false;
    }
  }, [key]);
  const [dismissed, setDismissed] = useState<string | null>(null);

  if (!key || alreadyDismissed || dismissed === key) return null;

  return (
    <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-1.5">
      <p className="min-w-0 flex-1 truncate text-sm">
        This week's count is due
      </p>
      {/* A link, not a button: it goes somewhere, and the browser should treat
          it as somewhere. Same treatment the alert screen's cross-links use. */}
      <Link
        to={countPath}
        className="shrink-0 text-sm font-semibold text-primary underline underline-offset-2"
      >
        Count now
      </Link>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Dismiss the count reminder for this week"
        onClick={() => {
          setDismissed(key);
          try {
            window.sessionStorage.setItem(key, "1");
          } catch {
            /* Dismissed for this render either way. */
          }
        }}
        className="-mr-1 shrink-0 text-muted-foreground"
      >
        <XIcon />
      </Button>
    </div>
  );
}

interface DraftLine {
  itemId: Id<"items">;
  name: string;
  packSize: string;
  qty: number;
  /**
   * Captured at add-time so the draft line can say what will happen to it.
   * A line the warehouse cannot cover is not refused — it goes to the manager
   * to order in, which is slower, and she is the only person who can decide
   * whether that is acceptable. She can only decide it if she is told.
   */
  warehouseAvailable: number;
}

/**
 * A failed query on this screen used to blank the page, because Convex throws
 * during render and nothing here caught it. On the one screen the whole demo
 * rests on, that is not a risk worth carrying.
 */
export function Request() {
  const [attempt, setAttempt] = useState(0);
  return (
    <ScreenBoundary
      key={attempt}
      title="Your items could not load"
      heading={<SectionHeading title="Request supplies" />}
      onRetry={() => setAttempt((n) => n + 1)}
    >
      <RequestScreen />
    </ScreenBoundary>
  );
}

function RequestScreen() {
  const palette = useQuery(api.catalogue.palette, {});
  const [term, setTerm] = useState("");
  // The box echoes every keystroke; the query does not. Typing "Cotton Roll"
  // used to be eleven round trips, ten of them thrown away before anyone read
  // them.
  const query = useDebouncedValue(term.trim());
  const searchResults = useQuery(
    api.catalogue.search,
    query.length >= 2 ? { term: query } : "skip",
  );
  const submit = useMutation(api.requests.submit);

  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [urgency, setUrgency] = useState<"routine" | "soon" | "urgent">("routine");
  const [notes, setNotes] = useState("");
  const [extrasOpen, setExtrasOpen] = useState(false);
  /* The busy flag, the re-entrancy guard and both toasts are the shared hook's
     — a double-tapped Send on a phone cannot fire two submissions. */
  const { run, busy: sending } = useBusyAction();
  /**
   * Which item went in on the last tap.
   *
   * The rehearsal's finding: the row you just tapped scrolls out of reach and
   * the screen never says *which* glove size is in the list. Glove sizes differ
   * by one word, so "3 items" is not confirmation. This is echoed in the send
   * bar, which is the one part of the screen that is always on the glass.
   */
  const [lastAdded, setLastAdded] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * cmdk always marks one row selected, including on first paint. A tint on
   * that row before anyone has touched anything reads as "this one is already
   * in your list", which is the opposite of true. So the highlight only turns
   * on once the person is actually navigating — a key, a pointer over the
   * list, or a search term. Nothing about *what* is selected changes; Enter
   * still adds the highlighted row from the first keystroke.
   */
  // React bails out when the value is unchanged, so calling this on every
  // pointermove costs one comparison, not a render.
  const [navigating, setNavigating] = useState(false);
  const engage = () => setNavigating(true);

  // Focused on mount so a keyboard user can type immediately. On a phone this
  // does not force the keyboard open — the list is what matters there, and a
  // keyboard covering the list would cost taps rather than save them.
  useEffect(() => {
    if (window.matchMedia("(min-width: 768px)").matches) inputRef.current?.focus();
  }, []);

  const list = term.trim().length >= 2 ? (searchResults ?? []) : (palette ?? []);
  // How many of each item are in the draft, not merely whether. The row badge
  // shows this number, which is what makes "tap it again for another"
  // something a person can watch happen rather than something they must be
  // told and then trust.
  const draftQty = useMemo(
    () => new Map(draft.map((line) => [line.itemId as string, line.qty])),
    [draft],
  );

  /**
   * Adding does **not** clear the search box any more.
   *
   * The screen says "Tap it again for another", and clearing the term made that
   * sentence false for exactly the case where it is most useful — you searched
   * for "Cotton Roll", tapped it, and the list you would tap again vanished. The
   * hint is now true on both paths, which is cheaper than rewriting the hint to
   * describe two different behaviours. Clearing the box is one tap on the
   * field's own clear control; un-clearing a list is a re-typed search.
   */
  const addItem = (item: (typeof list)[number]) => {
    setLastAdded(item.name);
    setDraft((current) => {
      const existing = current.find((line) => line.itemId === item._id);
      if (existing) {
        return current.map((line) =>
          line.itemId === item._id ? { ...line, qty: line.qty + 1 } : line,
        );
      }
      return [
        ...current,
        {
          itemId: item._id,
          name: item.name,
          packSize: item.packSize,
          // Their usual quantity, so the common case needs no stepper at all.
          qty: item.lastQty && item.lastQty > 0 ? item.lastQty : 1,
          warehouseAvailable: item.warehouseAvailable,
        },
      ];
    });
  };

  const setQty = (itemId: Id<"items">, qty: number) =>
    setDraft((current) =>
      qty <= 0
        ? current.filter((line) => line.itemId !== itemId)
        : current.map((line) => (line.itemId === itemId ? { ...line, qty } : line)),
    );

  const send = () => {
    if (draft.length === 0) return;
    return run(
      async () => {
        const result = await submit({
          lines: draft.map(({ itemId, qty }) => ({ itemId, qty })),
          urgency,
          notes: notes.trim() || null,
        });
        // Clearing the basket lives inside the action, after the await, so it
        // cannot run on the failure path. The draft is deliberately kept when
        // the send throws: losing somebody's basket because the network
        // blinked is the exact failure that gets a tool abandoned.
        setDraft([]);
        setNotes("");
        setUrgency("routine");
        setExtrasOpen(false);
        setLastAdded(null);
        setTerm("");
        return result;
      },
      {
        failed: "Could not send — your list is safe, try again in a moment",
        // `Sent — …` is load-bearing: `tools/clickcount.mjs` and
        // `tools/demo.mjs` both wait on /^Sent —/ to decide the request
        // actually went through.
        done: (result) => ({
          happened: `Sent — ${result.lineCount} ${plural(result.lineCount, "item")}`,
          next: "Your manager sees it now. It'll show on Requests until it arrives.",
        }),
      },
    );
  };

  if (palette === undefined) {
    return (
      <div data-screen-state="loading">
        <Loading rows={5} label="Loading your items" />
      </div>
    );
  }

  const totalUnits = draft.reduce((total, line) => total + line.qty, 0);
  // Resolved against the draft rather than remembered blind, so an item stepped
  // down to zero stops being announced as "added".
  const lastAddedLine = draft.find((line) => line.name === lastAdded) ?? null;
  const extrasSet = urgency !== "routine" || notes.trim().length > 0;

  return (
    <div data-screen-state="ready" className="space-y-3">
      {/* No subtitle. The header three centimetres up already says W68 and
          Dental assistant, and on a 390×844 phone a line that repeats it costs
          a row of the list — the only thing on this screen anybody came for. */}
      <SectionHeading title="Request supplies" />

      {/* v2's primary loop, advertised on v1's busiest screen — and passive:
          it adds no interaction to the measured path, takes no focus from the
          palette, and occupies one line so nothing moves below the fold. */}
      <CountDueBanner />

      {/* Two columns at `lg`, one at 390. A grid, not a reordering: the phone's
          DOM order survives untouched, so the palette is still the first thing
          under the title for a screen reader and for the tap counter. */}
      <div className="space-y-4 lg:grid lg:grid-cols-12 lg:items-start lg:gap-6 lg:space-y-0">
        <div className="lg:col-span-7">
          {/**
           * `src/components/ui/command.tsx` — the registry wrapper, on the
           * screen the whole demo rests on.
           *
           * This was raw `cmdk` on one argument: `CommandInput` wraps the field
           * in its own `div.h-9` and the field had to be 56px. The app is on
           * stock shadcn sizes now, so that objection is void and the palette
           * joins everything else.
           *
           * The registry is itself built on cmdk, so `[cmdk-item]` — which
           * `tools/clickcount.mjs` and `tools/demo.mjs` both select the palette
           * by — is rendered by exactly the same primitive as before, and
           * `onSelect` is still the one interaction that adds a line. Nothing
           * about the two-tap count changes.
           *
           * The one registry default that *is* wrong here is answered on the
           * item below rather than by staying off the registry: cmdk marks a
           * row selected on first paint, and `data-[selected=true]:bg-accent`
           * would tint it before anyone has touched anything.
           */}
          <Command
            shouldFilter={false}
            /* The registry `Command` is a `bg-popover` panel with no edge — it
               is built to sit inside a dialog. On a page it wears the registry
               card's edge instead.

               `focus-within` rather than a ring on the input: `CommandInput`
               renders `outline-hidden`, so the field draws no focus treatment
               of its own, and the palette is one control — the whole card
               should say it has the keyboard. The ring is the registry's own
               (`ring-ring/50` at 3px), not a private one. */
            className="rounded-xl border shadow-sm focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50"
            // Enter on a highlighted row adds it — keyboard parity with the tap.
            loop
            onKeyDown={engage}
          >
            <CommandInput
              ref={inputRef}
              value={term}
              onValueChange={setTerm}
              placeholder="Search all 97 items…"
              aria-label="Search items"
            />

            {/* 46vh on a phone keeps the draft in view under it. On a desktop
                the draft is beside it, not below, so the list runs as tall as
                the window allows rather than stranding whitespace under it. */}
            <CommandList
              onPointerMove={engage}
              className="max-h-[52vh] overscroll-contain lg:max-h-[calc(100dvh-15rem)]"
            >
              {term.trim().length >= 2 && searchResults === undefined ? (
                <div className="p-4">
                  <Loading rows={2} label="Searching" />
                </div>
              ) : null}

              {/* The registry spreads `props` after its own `className`, so a
                  passed string replaces the base outright rather than merging
                  with it — hence the base's three classes restated here beside
                  the two this call adds. */}
              <CommandEmpty className="px-6 py-6 text-center text-sm text-muted-foreground">
                {term.trim().length >= 2
                  ? `Nothing matching "${term}". Check the spelling, or ask your manager to add it.`
                  : "No items yet."}
              </CommandEmpty>

              {list.map((item) => {
                const inDraft = draftQty.get(item._id) ?? 0;
                return (
                  <CommandItem
                    key={item._id}
                    value={item._id}
                    onSelect={() => addItem(item)}
                    className={cn(
                      // A list row, not a menu row: full-bleed, ruled, and the
                      // whole thing is the tap target.
                      "cursor-pointer gap-3 rounded-none border-b px-4 py-3 last:border-0",
                      /* cmdk marks one row selected on first paint, and a tint
                         on it before anyone has touched anything reads as
                         "this one is already in your list". Same modifier and
                         same utility group as the registry's
                         `data-[selected=true]:bg-accent`, so tailwind-merge
                         drops that class outright until the person is actually
                         navigating — at which point the registry's own
                         selected surface is what shows. Nothing about *what*
                         is selected changes; Enter still adds the highlighted
                         row from the first keystroke. */
                      !navigating && "data-[selected=true]:bg-transparent",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm leading-tight font-medium">
                        {item.name}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                        <span>{item.packSize}</span>
                        {/* Quietest thing on the line: useful for recognising a
                            regular, never the reason to pick one. */}
                        {/* Was "ordered 3×", which reads either as "three on
                            order right now" or "three times ever" — and the
                            first reading is the one that changes what somebody
                            asks for. It is a history count, so it says so. */}
                        {item.timesRequested > 0 ? (
                          <span>
                            · asked for {item.timesRequested}{" "}
                            {plural(item.timesRequested, "time")} before
                          </span>
                        ) : null}
                        {/* A stock count is a number, not good news — in green
                            on every row it read as "plenty of everything", and
                            green already means *ordered* on the paper sheets.
                            The one thing on this line worth a colour is the
                            item that is not there. */}
                        {item.warehouseAvailable > 0 ? (
                          <span>· {item.warehouseAvailable} in warehouse</span>
                        ) : (
                          <span className="font-semibold text-red-700">
                            · none in warehouse
                          </span>
                        )}
                        {item.pricePending ? (
                          <span className="text-blue-700">· vendor promo</span>
                        ) : null}
                      </div>
                    </div>
                    {/* The whole row is the tap target, so this is an
                        indicator, not a button. Outlined when empty, filled
                        with the count once the item is in the draft, so
                        tapping again visibly counts up rather than doing
                        nothing you can see. Registry `icon-lg` geometry, in
                        the registry's own two primary surfaces. */}
                    <span
                      aria-hidden
                      className={cn(
                        "num flex size-10 shrink-0 items-center justify-center rounded-full text-base font-semibold",
                        inDraft > 0
                          ? "bg-primary text-primary-foreground"
                          : "border text-primary",
                      )}
                    >
                      {inDraft > 0 ? inDraft : "+"}
                    </span>
                    {inDraft > 0 ? (
                      <span className="sr-only">
                        {inDraft} in this request. Select again to add another.
                      </span>
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandList>
          </Command>
        </div>

        {/* The draft. At `lg` it is a sticky column with its own scroll, so the
            Send button is on screen at every cart length — the desktop
            equivalent of the fixed bar the phone gets. */}
        <div
          className="lg:sticky lg:top-[calc(var(--header-h)+1.5rem)] lg:col-span-5 lg:flex lg:max-h-[calc(100dvh-var(--header-h)-3rem)] lg:flex-col"
        >
          {draft.length > 0 ? (
            // `-mx-1 px-1` so a focus outline on a stepper is not shaved off by
            // the scroll container's edge.
            <div className="space-y-2 lg:-mx-1 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:px-1">
              <SubHeading>
                Sending {draft.length} {plural(draft.length, "item")} · {totalUnits}{" "}
                {plural(totalUnits, "unit")}
              </SubHeading>
              {draft.map((line) => (
                <Card
                  key={line.itemId}
                  /* Registry card, laid out by this call site rather than by
                     `CardHeader`/`CardContent`: `flex-row` beats the base's
                     `flex-col`, `gap-3` its `gap-6`, `p-3` its `py-6`. Same
                     rendered geometry the kit's <Card padding="p-3"> gave. */
                  className="flex-row items-center gap-3 p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{line.name}</div>
                    {/* The consequence, on the line it applies to. She is not
                        stopped and nothing is refused — but an item the
                        warehouse cannot cover has to be bought in, and finding
                        that out a week later is how a tool loses its user. */}
                    {line.warehouseAvailable <= 0 ? (
                      <div className="mt-0.5 text-xs font-semibold text-red-700">
                        Not in the warehouse — has to be ordered in
                      </div>
                    ) : (
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {line.packSize}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <QtyStepper
                      value={line.qty}
                      onChange={(next) => setQty(line.itemId, next)}
                      label={line.name}
                    />
                  </div>
                </Card>
              ))}

            </div>
          ) : (
            // Was a 115px dashed card, permanently, naming a "Send" button
            // that does not exist. One line, accurate, and it answers the
            // question the old card did not: how do I ask for three?
            <p className="px-1 text-sm text-muted-foreground">
              Tap an item to add it. Tap it again for another.
            </p>
          )}

          {/* Tap two. On a phone, fixed above the nav bar so it never needs
              scrolling to. At `lg` there is no nav bar down there and a
              full-width fixed bar ran under the sidebar, so `flowAt="lg"` makes
              it the foot of this column instead — same position in the DOM
              either way. This screen used to keep a private copy of the shared
              bar for that one difference; the difference is now a prop, and the
              bar's clearance comes with it. */}
          {draft.length > 0 ? (
            <ActionBar flowAt="lg">
                {/* What is actually in the list, on the one strip of the screen
                    that is never scrolled away. The rehearsal's finding was that
                    the item you just tapped drops below the fold and the screen
                    never names it — and glove sizes differ by a single word, so
                    a count is not confirmation. `aria-live` so it is spoken as
                    well as shown. */}
                <p
                  aria-live="polite"
                  className="mb-2 flex items-baseline gap-1.5 text-xs leading-tight text-muted-foreground"
                >
                  {lastAddedLine ? (
                    <>
                      <span className="shrink-0 font-semibold text-primary">
                        Added ×{lastAddedLine.qty}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                        {lastAddedLine.name}
                      </span>
                    </>
                  ) : (
                    <span className="min-w-0 flex-1 truncate">
                      {draft.map((line) => line.name).join(", ")}
                    </span>
                  )}
                  <span className="num shrink-0">
                    {totalUnits} {plural(totalUnits, "unit")}
                  </span>
                </p>

                {/* The urgency/note trigger lives *in* the bar, not behind it.
                    It sat in the draft column, which the taller bar then
                    covered — enabled, hit-testing to the strip, unreachable by
                    scrolling. Of the three available fixes (reserve padding,
                    make the strip transient, move the control) this is the one
                    that cannot regress: a control inside the thing that was
                    occluding it can never be occluded by it, at any bar height,
                    on any viewport, however the strip's text wraps. The padding
                    fix is in as well, because the *next* thing at the foot of
                    the basket would have hit the same wall — but padding is a
                    guarantee about layout, and this is a guarantee about the
                    one control the rehearsal could not reach.

                    Making the strip transient was rejected: it is the answer to
                    "which glove size did I add", and that question is asked
                    while looking at the Send button, not two seconds earlier. */}
                {/* A stock `outline` button. It used to be a hand-drawn dashed
                    box that then had to undo `focus-visible:border-ring` to
                    stop the registry repainting its edge; the registry's own
                    outline button is the same affordance with none of that. */}
                <Button
                  variant="outline"
                  onClick={() => setExtrasOpen(true)}
                  className="mb-2 w-full justify-between"
                >
                  {extrasSet ? (
                    <>
                      <UrgencyTag urgency={urgency} />
                      <span className="min-w-0 flex-1 truncate text-left">
                        {notes.trim() || "Routine"}
                      </span>
                      <span className="shrink-0 text-muted-foreground">Edit</span>
                    </>
                  ) : (
                    <span className="flex-1 text-center">
                      Add urgency, a date or a note
                    </span>
                  )}
                </Button>

                <Button
                  size="lg"
                  onClick={send}
                  disabled={sending}
                  className="w-full"
                >
                  {sending
                    ? "Sending…"
                    : `Send request · ${draft.length} ${plural(draft.length, "item")}`}
                </Button>
            </ActionBar>
          ) : null}
        </div>
      </div>

      {/*
        Urgency and the note, above every bar on the screen.

        "Before Friday" has no home in the data — urgency is three fixed levels
        and there is no date on `requests` — so rather than invent one, the note
        is labelled as the place a date goes and the field says so in its own
        placeholder. A manager reading "needed before Friday" in the note does
        the right thing; a date field nobody can query would only look like it
        did more.
      */}
      <Sheet
        open={extrasOpen}
        onClose={() => setExtrasOpen(false)}
        title="How urgent, and by when?"
        description="Optional. Your request sends fine without it."
        footer={
          <Button size="lg" className="w-full" onClick={() => setExtrasOpen(false)}>
            Done
          </Button>
        }
      >
        <div className="space-y-4 pb-2">
          <div>
            <SubHeading>How urgent?</SubHeading>
            {/* Stock buttons, `size="lg"` for the same reason kit's `<Chips>`
                is: a segmented picker in a sheet is a phone control a gloved
                thumb aims at. **`aria-pressed` is load-bearing** — it is what
                makes a pair of buttons a segmented toggle to assistive tech,
                and it survived the restyle deliberately. */}
            <div className="mt-2 flex gap-2">
              {(["routine", "soon", "urgent"] as const).map((level) => (
                <Button
                  key={level}
                  variant={urgency === level ? "default" : "outline"}
                  size="lg"
                  onClick={() => setUrgency(level)}
                  aria-pressed={urgency === level}
                  className="flex-1 capitalize"
                >
                  {level}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <SubHeading>Need it by a date? Say so here</SubHeading>
            {/* shadcn's <Textarea>, with exactly one registry default undone:

                  `field-sizing-content` → `field-sizing-fixed`. The registry
                  grows the box with its content, which ignores `rows` and
                  changes the sheet's height mid-keystroke. `rows={3}` stays
                  the size of this field.

                The edge, the fill, the radius, the shadow and the focus ring
                are all the registry's now. The 16px iOS floor holds without
                help: the base is `text-base md:text-sm`, so it is only 14px
                from 768px up, which is not a phone.

                The accessible name stays on `aria-label`: the visible label is
                the <SubHeading> above, which is the section heading for this
                block, and the spoken name says more than it does. */}
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="e.g. before Friday — Dr. Jung's list"
              rows={3}
              aria-label="Note for your manager, including any date you need it by"
              className="field-sizing-fixed mt-2"
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              Your manager reads this with the request. There is no date field —
              a date written here is the date they act on.
            </p>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
